import { DestroyRef, Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { WindowRef } from '@boels/helpers';
import { CultureFacade } from '@boels/shared-state';
import { combineLatest, map, take } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppConfig } from '@boels/models';

declare global {
  interface Window {
    embedded_svc: any;
  }
}
interface WidgetInstance {
  culture: string;
  cultureCode: string;
  buttonId: string;
}

@Injectable()
export class SalesForceChatService {
  #document = inject<Document>(DOCUMENT);
  #winRef = inject(WindowRef);
  #cultureFacade = inject(CultureFacade);
  #translocoService = inject(TranslocoService);
  #destroyRef = inject(DestroyRef);
  #environment = inject<AppConfig>('ENVIRONMENT' as any);

  private readonly widgetInstances: WidgetInstance[] = [
    {
      culture: 'nl-nl',
      cultureCode: 'Boels_com_NL',
      buttonId: this.#environment.salesForceChat.widgetButtonsId.NLButtonId,
    },
    {
      culture: 'nl-be',
      cultureCode: 'Boels_com_NL',
      buttonId: this.#environment.salesForceChat.widgetButtonsId.NLButtonId,
    },
    {
      culture: 'en-nl',
      cultureCode: 'Boels_com_NL',
      buttonId: this.#environment.salesForceChat.widgetButtonsId.NLButtonId,
    },
    {
      culture: 'fr-be',
      cultureCode: 'Boels_com_FR',
      buttonId: this.#environment.salesForceChat.widgetButtonsId.FRButtonId,
    },
    {
      culture: 'fr-lu',
      cultureCode: 'Boels_com_FR',
      buttonId: this.#environment.salesForceChat.widgetButtonsId.FRButtonId,
    },
  ];

  init(): void {
    if (!this.#document.defaultView || !this.#winRef.isBrowser()) {
      return;
    }

    this.getWidgetInstance()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((widget) => {
        this.removeAllChatInstances();
        if (widget) {
          if (
            this.#document.defaultView?.embedded_svc?.settings.buttonId &&
            this.#winRef.location?.href
          ) {
            this.#winRef.replaceWindowLocationForCulture(widget.culture);
          }
          this.initChat(widget);
        }
      });
  }

  getWidgetInstance() {
    return this.#cultureFacade
      .getActiveCulture()
      .pipe(
        map((data: string) =>
          this.widgetInstances.find((instance) => instance.culture === data),
        ),
      );
  }

  initChat(widget: WidgetInstance) {
    if (!this.#document.defaultView?.embedded_svc) {
      const s = document.createElement('script');
      s.setAttribute(
        'src',
        'https://boelsrental.my.salesforce.com/embeddedservice/5.0/esw.min.js',
      );
      s.onload = () => this.initESW(widget, null);
      document.body.appendChild(s);
    } else {
      this.initESW(widget, 'https://service.force.com');
    }
  }

  initESW(widget: WidgetInstance, gslbBaseURL: string | null): void {
    const embedded_svc = this.#document.defaultView?.embedded_svc || {};
    embedded_svc.settings.displayHelpButton = true; //Or false
    embedded_svc.settings.language = widget.culture; //For example, enter 'en' or 'en-US'

    embedded_svc.settings.enabledFeatures = ['LiveAgent'];
    embedded_svc.settings.entryFeature = 'LiveAgent';

    combineLatest([
      this.#translocoService.selectTranslate(
        'defaultMinimizedText',
        {},
        { scope: 'salesforceChat' },
      ),
      this.#translocoService.selectTranslate(
        'disabledMinimizedText',
        {},
        { scope: 'salesforceChat' },
      ),
      this.#translocoService.selectTranslate(
        'loadingText',
        {},
        { scope: 'salesforceChat' },
      ),
      this.#translocoService.selectTranslate(
        'offlineSupportMinimizedText',
        {},
        { scope: 'salesforceChat' },
      ),
      this.#translocoService.selectTranslate(
        'defaultAssistiveText',
        {},
        { scope: 'salesforceChat' },
      ),
    ])
      .pipe(
        map((chunks: string[]) => {
          [
            window.embedded_svc.settings.defaultMinimizedText,
            window.embedded_svc.settings.disabledMinimizedText,
            window.embedded_svc.settings.loadingText,
            window.embedded_svc.settings.offlineSupportMinimizedText,
            window.embedded_svc.settings.defaultAssistiveText,
          ] = [...chunks];
        }),
        take(1),
      )
      .subscribe();

    embedded_svc.init(
      `https://${this.#environment.salesForceChat.widgetBaseURI}${this.#environment.salesForceChat.sandBox}.my.salesforce.com`,
      `https://${this.#environment.salesForceChat.widgetBaseURI}${this.#environment.salesForceChat.sandBox}.my.salesforce-sites.com/liveAgentSetupFlow`,
      gslbBaseURL,
      this.#environment.salesForceChat.hashCode,
      widget.cultureCode,
      {
        baseLiveAgentContentURL: `https://c.${this.#environment.salesForceChat.baseLiveAgentURI}/content`,
        deploymentId: this.#environment.salesForceChat.deploymentId,
        buttonId: widget.buttonId,
        baseLiveAgentURL: `https://d.${this.#environment.salesForceChat.baseLiveAgentURI}/chat`,
        eswLiveAgentDevName: widget.cultureCode,
        isOfflineSupportEnabled: false,
      },
    );
  }

  removeAllChatInstances() {
    if (this.#document.defaultView?.embedded_svc) {
      this.#winRef.nativeWindow?.window.embedded_svc?.hideHelpButton();
    }
  }
}
